import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Alert from "@material-ui/lab/Alert";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import { REGION_OPTIONS, SERVER_LOCATIONS_BY_REGION, getRegionTimeZone } from "./matchNightOptions";
import { regionInputValueToUtcDate, utcDateToRegionInputValue } from "./matchNightTime";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const dialogPaperStyle = { backgroundColor: "#1e2023", color: "#fff", minWidth: 520 };

function getInitialFormState(night) {
  if (!night) {
    return {
      scheduledAtLocal: "",
      region: "EU",
      serverLocations: [],
      minToFire: "10",
      maxCapacity: "20",
      published: false,
      title: "",
      notes: "",
    };
  }
  return {
    scheduledAtLocal: utcDateToRegionInputValue(new Date(night.scheduledAt), night.region),
    region: night.region,
    serverLocations: night.serverLocations || [],
    minToFire: String(night.minToFire),
    maxCapacity: String(night.maxCapacity),
    published: night.status === "published",
    title: night.title || "",
    notes: night.notes || "",
  };
}

export default function MatchNightFormDialog({ open, night, user, onClose, onSaved }) {
  const [form, setForm] = useState(getInitialFormState(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(getInitialFormState(night));
      setError(null);
    }
  }, [open, night]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleRegionChange(region) {
    const options = SERVER_LOCATIONS_BY_REGION[region] || [];
    const allowed = new Set(options.map((loc) => loc.value));
    setForm((prev) => {
      const kept = prev.serverLocations.filter((loc) => allowed.has(loc));
      return {
        ...prev,
        region,
        serverLocations: kept.length > 0 ? kept : options.length === 1 ? [options[0].value] : [],
      };
    });
  }

  function toggleServerLocation(value) {
    setForm((prev) => ({
      ...prev,
      serverLocations: prev.serverLocations.includes(value)
        ? prev.serverLocations.filter((loc) => loc !== value)
        : [...prev.serverLocations, value],
    }));
  }

  const isValid =
    form.scheduledAtLocal &&
    form.serverLocations.length > 0 &&
    Number(form.minToFire) >= 1 &&
    Number(form.maxCapacity) >= Number(form.minToFire);

  async function handleSave() {
    if (!isValid || !user?.userId) return;
    setSaving(true);
    setError(null);

    const scheduledAtUtc = regionInputValueToUtcDate(form.scheduledAtLocal, form.region);
    const body = {
      scheduledAt: scheduledAtUtc ? scheduledAtUtc.toISOString() : null,
      region: form.region,
      serverLocations: form.serverLocations,
      minToFire: Number(form.minToFire),
      maxCapacity: Number(form.maxCapacity),
      status: form.published ? "published" : "draft",
      title: form.title.trim() || null,
      notes: form.notes.trim() || null,
    };

    try {
      const url = night
        ? `${basePath}/api/v7/admin/match-nights/${night.id}/adpass/${user.userId}`
        : `${basePath}/api/v7/admin/match-nights/adpass/${user.userId}`;
      const resp = await fetch(url, {
        method: night ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (data.ok) {
        onSaved();
      } else {
        setError(data.message || "Something went wrong. Try again.");
      }
    } catch (_) {
      setError("Something went wrong. Try again.");
    } finally {
      setSaving(false);
    }
  }

  const serverLocationOptions = SERVER_LOCATIONS_BY_REGION[form.region] || [];
  const timeAbbreviation = getRegionTimeZone(form.region).abbreviation;

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ style: dialogPaperStyle }} maxWidth="sm" fullWidth>
      <DialogTitle style={{ color: "#fff" }}>
        {night ? "Edit match night" : "New match night"}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={`Scheduled time (${timeAbbreviation})`}
              type="datetime-local"
              value={form.scheduledAtLocal}
              onChange={(e) => updateField("scheduledAtLocal", e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              InputLabelProps={{ shrink: true, style: { color: "rgba(255,255,255,0.6)" } }}
              inputProps={{ style: { color: "#fff" } }}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl variant="outlined" size="small" fullWidth disabled={saving}>
              <InputLabel style={{ color: "rgba(255,255,255,0.6)" }}>Region</InputLabel>
              <Select
                value={form.region}
                onChange={(e) => handleRegionChange(e.target.value)}
                label="Region"
                style={{ color: "#fff" }}
              >
                {REGION_OPTIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.published}
                  onChange={(e) => updateField("published", e.target.checked)}
                  color="primary"
                  disabled={saving}
                />
              }
              label={
                <span style={{ color: "rgba(255,255,255,0.8)" }}>
                  {form.published ? "Published" : "Draft"}
                </span>
              }
            />
            <Typography style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", marginTop: 4 }}>
              {form.published
                ? "Visible on the public /match-night page."
                : "Hidden from the public /match-night page until published."}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", marginBottom: 4 }}>
              Server locations
            </Typography>
            <FormGroup row>
              {serverLocationOptions.map((loc) => (
                <FormControlLabel
                  key={loc.value}
                  control={
                    <Checkbox
                      checked={form.serverLocations.includes(loc.value)}
                      onChange={() => toggleServerLocation(loc.value)}
                      color="primary"
                      size="small"
                      disabled={saving}
                    />
                  }
                  label={<span style={{ color: "rgba(255,255,255,0.85)" }}>{loc.label}</span>}
                />
              ))}
            </FormGroup>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Min to fire"
              type="number"
              value={form.minToFire}
              onChange={(e) => updateField("minToFire", e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
              inputProps={{ min: 1, style: { color: "#fff" } }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Max capacity"
              type="number"
              value={form.maxCapacity}
              onChange={(e) => updateField("maxCapacity", e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
              inputProps={{ min: 1, style: { color: "#fff" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Title (optional)"
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
              inputProps={{ style: { color: "#fff" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes (optional, internal)"
              value={form.notes}
              onChange={(e) => updateField("notes", e.target.value)}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size="small"
              disabled={saving}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
              inputProps={{ style: { color: "#fff" } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px" }}>
        <Button onClick={onClose} disabled={saving} style={{ color: "rgba(255,255,255,0.6)" }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !isValid}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MatchNightFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  night: PropTypes.object,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

MatchNightFormDialog.defaultProps = {
  night: null,
  user: null,
};
